import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SHORTCUT_CONST } from '@/constant';
import {
  Cancel,
  CheckBox,
  CheckBoxOutlineBlank,
  Close,
  FileDownload,
  FileUpload,
  Help1,
  PREFIX_STRIP_REGEXP,
  Plus,
  SettingsBackupRestore,
  VBasePopover,
  VButton,
  VDialog,
  VFlex,
  VIconButton,
  VInput,
  VStack,
  cn,
  useShortcut,
  useVToast,
} from '@/package';
import { useToolSettingStore } from '@/store';
import { isValidShortcutMap } from '@/util';

const createKeyboardShortcutString = (event: KeyboardEvent): string[] => {
  const modifiers: string[] = [];

  if (event.ctrlKey) modifiers.push('Ctrl');
  if (event.metaKey) modifiers.push('Cmd');
  if (event.altKey) modifiers.push('Alt');
  if (event.shiftKey) modifiers.push('Shift');

  const isModifierKey = ['Control', 'Meta', 'Alt', 'Shift'].includes(event.key);
  if (!isModifierKey && event.code) {
    modifiers.push(event.code);
  }

  return modifiers;
};

const ShortcutSettingModal = () => {
  const { shortcutPopupVisible, setShortcutPopupVisible } =
    useToolSettingStore();
  const [searchTerm, setSearchTerm] = useState('');

  return (
    <VDialog
      visible={shortcutPopupVisible}
      onUpdateVisible={setShortcutPopupVisible}
      dialogMainClass="w-[824px] h-[657px]"
    >
      <VStack className="size-full bg-white p-6" gap={1}>
        <VFlex items="center" justify="between">
          <div className="text-headline-md font-bold">Customize shortcuts</div>
          <Close
            className="size-4 text-gray-400"
            role="button"
            onClick={() => setShortcutPopupVisible(false)}
          />
        </VFlex>

        <VStack className="flex-1 overflow-y-hidden" gap={0.5}>
          <ShortcutSettingOperation
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
          <ShortcutSettingTable searchTerm={searchTerm} />
        </VStack>
      </VStack>
    </VDialog>
  );
};

const ShortcutSettingOperation = ({
  searchTerm,
  setSearchTerm,
}: {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}) => {
  const { reset, exportShortcut, importShortcut } = useShortcut();
  const { addToast } = useVToast();
  const { t } = useTranslation();

  return (
    <VFlex className="w-full" justify="between" items="center">
      <div className="w-[239px]">
        <VInput
          size="sm"
          type="search"
          value={searchTerm}
          fluid
          onChange={e => {
            setSearchTerm(e.target.value);
          }}
          placeholder={t('shortcut.searchInputPlaceholder.consentful')}
        />
      </div>

      <VFlex gap={0.25}>
        <VIconButton
          Icon={SettingsBackupRestore}
          outlined
          size="md"
          variant="secondary"
          onClick={() => reset()}
        />
        <VIconButton
          Icon={Help1}
          outlined
          size="md"
          variant="secondary"
          onClick={() => {
            window.open(
              `${window.location.origin}/help/mouse-keyboard`,
              '_blank',
            );
          }}
        />
        <VButton size="md" onClick={() => exportShortcut()}>
          <VFlex gap={0.25} items="center">
            <FileUpload className="size-4" />
            {t('shortcut.exportShortcutButtonText.obfuscate')}
          </VFlex>
        </VButton>

        <VButton
          size="md"
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.json';
            input.onchange = async event => {
              const file = (event.target as HTMLInputElement).files?.[0];
              if (!file) return;

              try {
                const text = await file.text();
                const parsedData = JSON.parse(text);

                importShortcut(isValidShortcutMap(parsedData));
              } catch {
                addToast({
                  message: t('shortcut.enterShortcutPlaceholder.comprecation'),
                  variant: 'error',
                });
              }
            };
            input.click();
          }}
        >
          <VFlex gap={0.25} items="center">
            <FileDownload className="size-4" />
            {t('shortcut.importShortcutButtonText.stomatodaeum')}
          </VFlex>
        </VButton>
      </VFlex>
    </VFlex>
  );
};

const ShortcutSettingTable = ({ searchTerm }: { searchTerm: string }) => {
  const { t } = useTranslation();
  const { shortcutList } = useShortcut();

  const searchFilteredShortcutItemList = !searchTerm.trim()
    ? shortcutList
    : shortcutList.filter(item => {
        const { title, category, key } = item;

        const searchLower = searchTerm.toLowerCase();

        const categoryName = category ? t(category).toLowerCase() : '';
        if (categoryName.includes(searchLower)) return true;

        const functionName = title ? t(title).toLowerCase() : '';
        if (functionName.includes(searchLower)) return true;

        const shortcutTexts = key.map(shortcut => shortcut).join(' ');
        if (shortcutTexts.includes(searchLower)) return true;

        return false;
      });

  const filteredShortcutItemList = searchFilteredShortcutItemList.filter(
    ({ command }) => {
      // command 가 undefined 이어서 발생한 Exception 이 있었음
      return !command?.match(/changeView(\d+)|indexToggle/);
    },
  );

  return (
    <VStack className="h-full w-full flex-1 divide-y divide-gray-300 overflow-y-auto rounded-md border border-gray-300">
      <ShortcutSettingTableHeader />
      {filteredShortcutItemList.map(item => {
        const { command, key, title, category, inactivated } = item;
        return (
          <ShortcutSettingTableRow
            command={command}
            categoryName={category ? t(category) : ''}
            functionName={title ? t(title) : ''}
            inactivated={inactivated}
            key={command + key.join('')}
            keyList={key}
          />
        );
      })}
    </VStack>
  );
};

const ShortcutSettingTableHeader = () => {
  return (
    <VFlex className="w-full divide-x divide-gray-300 text-body-md font-bold text-gray-400">
      <div className="w-[164px] p-2">Category</div>
      <div className="flex-1 p-2">Function Name</div>
      <div className="flex-1 p-2">Shortcut</div>
      <div className="w-[62px] p-2">Activate</div>
    </VFlex>
  );
};

const ShortcutSettingTableRow = ({
  categoryName,
  functionName,
  keyList,
  command,
  inactivated = false,
}: {
  categoryName: string;
  functionName: string;
  keyList: string[];
  command: string;
  inactivated?: boolean;
}) => {
  const [isAddingShortcut, setIsAddingShortcut] = useState(false);
  const isEmpty = keyList.length === 0;

  return (
    <VFlex className="w-full divide-x divide-gray-300 text-body-md">
      <div className="w-[164px] p-2">{categoryName}</div>
      <div className="flex-1 p-2">{functionName}</div>
      <div className="flex-1 px-2 py-1">
        <VFlex gap={0.25} className="w-full" items="start">
          <VStack className="flex-1" gap={0.25}>
            {!isEmpty ? (
              keyList.map((v, index) => {
                const keyText = v;
                const keyArr = keyText.split('+').map(v => v.trim());

                return (
                  <ShortcutChangeInput
                    key={index}
                    initialShortcut={keyArr}
                    command={command}
                    keyIndex={index}
                  />
                );
              })
            ) : (
              <ShortcutChangeInput
                initialShortcut={[]}
                command={command}
                keyIndex={0}
              />
            )}
            {isAddingShortcut && (
              <ShortcutChangeInput
                initialShortcut={[]}
                command={command}
                isAddingShortcut
                keyIndex={keyList.length}
                setIsAddingShortcut={setIsAddingShortcut}
              />
            )}
          </VStack>
          <VIconButton
            disabled={isEmpty}
            Icon={Plus}
            size="sm"
            variant="roundPrimary"
            onClick={() => setIsAddingShortcut(true)}
          />
        </VFlex>
      </div>
      <div className="w-[62px] p-2">
        <ShortcutActivateButton command={command} inactivated={inactivated} />
      </div>
    </VFlex>
  );
};

const ShortcutChangeInput = ({
  initialShortcut,
  command,
  keyIndex,
  isAddingShortcut = false,
  setIsAddingShortcut,
}: {
  initialShortcut: string[];
  command: string;
  keyIndex: number;
  isAddingShortcut?: boolean;
  setIsAddingShortcut?: (isAddingShortcut: boolean) => void;
}) => {
  const [shortcutValue, setShortcutValue] = useState<string[]>(initialShortcut);
  const { changeShortcut, isDuplicateShortcut, addShortcutKey } = useShortcut();
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const [isError, setIsError] = useState(false);
  const isEmpty = initialShortcut.length === 0;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    setShortcutValue([]);

    if (event.nativeEvent.isComposing) return;
    if (event.nativeEvent.key === 'Process') return;

    event.preventDefault();

    const shortcutString = createKeyboardShortcutString(event.nativeEvent);
    if (
      isDuplicateShortcut(
        SHORTCUT_CONST.SHORTCUT_NAMESPACE.LABELING,
        shortcutString.join('+'),
      )
    ) {
      setIsError(true);
    } else {
      setIsError(false);
    }

    setShortcutValue(shortcutString);
  };

  return (
    <div className="relative h-fit">
      <VBasePopover
        trigger="focus"
        placement="bottom"
        content={() => (
          <ShortcutPopoverContent variant={isError ? 'error' : 'default'} />
        )}
      >
        <VInput
          size="sm"
          fluid
          value={shortcutValue.join('+').replace(PREFIX_STRIP_REGEXP, '')}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            if (isError) {
              setShortcutValue(initialShortcut);
              return;
            }

            if (isEmpty || isAddingShortcut) {
              setIsAddingShortcut?.(false);
              shortcutValue.length > 0 &&
                addShortcutKey(command as any, shortcutValue.join('+'));
            } else {
              changeShortcut(command as any, shortcutValue.join('+'), keyIndex);
            }
          }}
          placeholder={t('shortcut.enterShortcutPlaceholder.comprecation')}
        />
      </VBasePopover>
      {isFocused && (
        <Cancel
          className="absolute top-1/2 right-2 size-4 -translate-y-1/2 rounded-full"
          role="button"
          onMouseDown={e => {
            e.preventDefault();
          }}
          onClick={e => {
            e.stopPropagation();
            setShortcutValue(() => []);
            !isAddingShortcut && changeShortcut(command as any, '', keyIndex);
            setIsFocused(false);
          }}
        />
      )}
    </div>
  );
};

const ShortcutPopoverContent = ({
  variant = 'default',
}: {
  variant?: 'default' | 'error';
}) => {
  const { t } = useTranslation();
  const message =
    variant === 'error'
      ? t('shortcut.shortcutDuplicateErrorMessage.contaminant')
      : t('shortcut.shortcutEditDescription.convalesce');
  return (
    <div
      className={cn(
        'max-w-[259px] bg-black px-2 py-1 text-body-md wrap-break-word',
        {
          'text-white': variant === 'default',
          'text-error-500': variant === 'error',
        },
      )}
    >
      {message}
    </div>
  );
};

const ShortcutActivateButton = ({
  command,
  inactivated,
}: {
  command: string;
  inactivated: boolean;
}) => {
  const { activate, inactivate } = useShortcut();
  const Icon = inactivated ? CheckBoxOutlineBlank : CheckBox;

  return (
    <VFlex className="w-full" items="center" justify="center">
      <Icon
        className="size-5"
        role="button"
        onClick={() => {
          inactivated
            ? activate(
                SHORTCUT_CONST.SHORTCUT_NAMESPACE.LABELING,
                command as any,
              )
            : inactivate(
                SHORTCUT_CONST.SHORTCUT_NAMESPACE.LABELING,
                command as any,
              );
        }}
      />
    </VFlex>
  );
};

export default ShortcutSettingModal;
